import React, { useEffect, useReducer } from 'react';
import {
  IconButton, SelectPicker, Stack,
} from 'rsuite';
import { Icon } from '@rsuite/icons';
import { FaFileExcel } from 'react-icons/fa';
import {
  downloadPricingRuleTemplate, getBaseMarketplaces, importPricingRules, XLSX_MIME_TYPE,
} from '../../api/pricingRules/pricingRules';
import FileUploadCsv from '../FileUploadCsv';
import { downloadData } from '../../utils/downloadData';
import handleError from '../../api/apiErrorHandle';

const TEMPLATE_FILE_NAME = 'pricing-rule-template.xlsx';

interface State {
  baseMarketplaces: string[];
  selectedBaseMarketplace: string | null;
  downloading: boolean;
}

const initialState: State = {
  baseMarketplaces: [],
  selectedBaseMarketplace: null,
  downloading: false,
};

type MarketplacesLoadedAction = { type: 'marketplaces/loaded', payload: string[] };
type MarketplaceSelectedAction = { type: 'marketplace/selected', payload: string | null };
type DownloadStartAction = { type: 'download/start' };
type DownloadEndAction = { type: 'download/end' };
type Action =
  | MarketplacesLoadedAction
  | MarketplaceSelectedAction
  | DownloadStartAction
  | DownloadEndAction;

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'marketplaces/loaded':
      return {
        ...state,
        baseMarketplaces: action.payload,
        selectedBaseMarketplace: action.payload[0] ?? null,
      };
    case 'marketplace/selected':
      return { ...state, selectedBaseMarketplace: action.payload };
    case 'download/start':
      return { ...state, downloading: true };
    case 'download/end':
      return { ...state, downloading: false };
    default:
      return state;
  }
}

export default function AllegroPriceRules() {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { baseMarketplaces, selectedBaseMarketplace, downloading } = state;

  useEffect(() => {
    const controller = new AbortController();

    getBaseMarketplaces(controller.signal)
      .then(data => dispatch({ type: 'marketplaces/loaded', payload: data }))
      .catch(handleError);

    return () => controller.abort();
  }, []);

  function selectBaseMarketplace(value: string | null) {
    dispatch({ type: 'marketplace/selected', payload: value });
  }

  async function downloadTemplate() {
    if (!selectedBaseMarketplace) {
      return;
    }

    dispatch({ type: 'download/start' });
    try {
      const blob = await downloadPricingRuleTemplate(selectedBaseMarketplace);
      downloadData(blob, XLSX_MIME_TYPE, TEMPLATE_FILE_NAME);
    } catch (error) {
      handleError(error);
    } finally {
      dispatch({ type: 'download/end' });
    }
  }

  async function uploadFile(file: Blob) {
    if (!selectedBaseMarketplace) {
      return undefined;
    }

    const data = new FormData();
    data.append('attachment', file);
    await importPricingRules(selectedBaseMarketplace, data);

    return undefined;
  }

  return (
    <div className="mt-20">
      <Stack spacing={20}>
        <SelectPicker
          style={{ width: 240 }}
          label="Base marketplace"
          searchable={false}
          cleanable={false}
          data={baseMarketplaces.map(m => ({ value: m, label: m }))}
          value={selectedBaseMarketplace}
          onChange={value => selectBaseMarketplace(value)}
        />
        <IconButton
          appearance="primary"
          icon={<Icon as={FaFileExcel} />}
          loading={downloading}
          disabled={!selectedBaseMarketplace}
          onClick={() => downloadTemplate()}
        >
          Download template
        </IconButton>
      </Stack>
      <FileUploadCsv
        className="mt-20"
        getUploadResult={file => uploadFile(file)}
        accept={['.xlsx']}
        disableUploadButton={!selectedBaseMarketplace}
      />
    </div>
  );
}
